"use client";

import { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import { services } from "data/services";
import emailjs from "@emailjs/browser";

const EMAILJS_SERVICE_ID = "service_fb62hdu";
const EMAILJS_TEMPLATE_ID = "template_q6e91bh";
const EMAILJS_PUBLIC_KEY = "jQox7T4KkBgO0RSrg";

const ServiceBundling = ({ className = "" }) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);

  useEffect(() => setIsHydrated(true), []);

  // Lock scroll
  useEffect(() => {
    document.body.style.overflow = showModal ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [showModal]);

  const validateEmail = (value) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) return "Email is required";
    if (!re.test(value)) return "Enter a valid email address";
    return "";
  };

  const toggleService = (id) => {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const totalPrice = services
    .filter((s) => selectedServices.includes(s.id))
    .reduce((sum, s) => sum + s.price, 0);

  const bundleDiscount =
    selectedServices.length >= 3
      ? 0.15
      : selectedServices.length >= 2
      ? 0.1
      : 0;

  const discountedPrice = totalPrice * (1 - bundleDiscount);
  const savings = totalPrice - discountedPrice;

  const selectedDetails = services
    .filter((s) => selectedServices.includes(s.id))
    .map((s) => `${s.title} - KES ${s.price.toLocaleString()}`)
    .join("<br>");

  const templateParams = {
    user_email: email,
    selected_services: selectedDetails,
    subtotal: `KES ${totalPrice.toLocaleString()}`,
    bundle_discount: `${(bundleDiscount * 100).toFixed(0)}%`,
    savings: `KES ${savings.toLocaleString()}`,
    total_investment: `KES ${discountedPrice.toLocaleString()}`,
  };

  const handleSendQuote = async () => {
    const err = validateEmail(email);
    if (err) {
      setEmailError(err);
      return;
    }

    setEmailError("");
    setLoading(true);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY,
      );
      setQuoteSent(true);
    } catch (e) {
      alert("Failed to send quote.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (!isHydrated) return <div>Loading...</div>;
  return (
    <section className={`py-2  ${className}`}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-headline font-black text-foreground mb-2">
            Build Your <span className="text-red-600">Custom</span> Package
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Select multiple services and save. The more you bundle, the more you
            save.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4 mb-2">
          {services.map((service) => {
            const isSelected = selectedServices.includes(service.id);
            return (
              <button
                key={service.id}
                onClick={() => toggleService(service.id)}
                className={`p-4 rounded-2xl shadow-subtle transition-all duration-300 text-left ${
                  isSelected
                    ? "border-primary bg-white shadow-dramatic"
                    : "border-border bg-card hover:border-primary/50 hover:shadow-subtle"
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 shadow-subtle rounded-xl hidden sm:flex items-center justify-center">
                    <Icon
                      name={service.iconName}
                      size={24}
                      className={
                        isSelected ? "text-red-600" : "text-foreground"
                      }
                    />
                  </div>
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      isSelected
                        ? "border-red-600 bg-red-600"
                        : "border-border bg-white"
                    }`}
                  >
                    {isSelected && (
                      <Icon name="CheckIcon" size={16} className="text-white" />
                    )}
                  </div>
                </div>
                <h3
                  className={`text-sm sm:text-lg font-headline font-bold mb-2 ${
                    isSelected ? "text-red-600" : "text-foreground"
                  }`}
                >
                  {service.title}
                </h3>
                <p className="text-sm sm:text-xl font-headline font-black text-primary">
                  KES {service.price.toLocaleString()}
                </p>
              </button>
            );
          })}
        </div>

        {/* Custom Bundle Summary */}
        {selectedServices.length > 0 && (
          <div className="bg-card rounded-2xl shadow-dramatic p-2 lg:p-12 mt-2">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8 md:gap-12">
              {/* Selected Services */}
              <div>
                <h3 className="text-2xl sm:text-4xl font-headline font-black text-foreground mb-2">
                  Your <span className="text-red-600">Custom</span> Bundle
                </h3>
                <div className="space-y-1 sm:space-y-2 md:space-y-4">
                  {services
                    .filter((s) => selectedServices.includes(s.id))
                    .map((service) => (
                      <div
                        key={service.id}
                        className="flex items-center justify-between py-3 border-b border-border"
                      >
                        <div className="flex items-center space-x-3">
                          <Icon
                            name={service.iconName}
                            size={20}
                            className="text-primary"
                          />
                          <span className="text-sm sm:text-xl font-bold text-foreground">
                            {service.title}
                          </span>
                        </div>
                        <span className="text-sm sm:text-xl font-headline font-bold text-foreground">
                          KES {service.price.toLocaleString()}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Pricing Summary & Request Button */}
              <div className="bg-gradient-to-br from-primary to-accent rounded-xl p-4 lg:p-8 text-white">
                <div className="space-y-2 sm:space-y-4 md:space-y-6">
                  <div>
                    <p className="text-sm opacity-80 mb-2">Subtotal</p>
                    <p className="text-xl sm:text-2xl font-headline font-black">
                      KES {totalPrice.toLocaleString()}
                    </p>
                  </div>

                  {bundleDiscount > 0 && (
                    <div className="py-2 sm:py-4 border-t border-white/20">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm opacity-80">Bundle Discount</p>
                        <span className="px-3 py-1 bg-secondary text-foreground text-xs font-headline font-bold rounded-full">
                          {(bundleDiscount * 100).toFixed(0)}% OFF
                        </span>
                      </div>
                      <p className="text-xl sm:text-2xl font-headline font-black">
                        -KES {savings.toLocaleString()}
                      </p>
                    </div>
                  )}

                  <div className="py-2 sm:py-4 border-t border-white/20">
                    <p className="text-sm opacity-80 mb-2">Total Investment</p>
                    <p className="text-xl sm:text-3xl font-headline font-black">
                      KES {discountedPrice.toLocaleString()}
                    </p>
                  </div>

                  <button
                    onClick={() => setShowModal(true)}
                    disabled={selectedServices.length < 2}
                    className={`w-full px-6 py-2 font-headline font-bold text-base rounded-lg flex items-center justify-center space-x-2 transition
    ${
      selectedServices.length >= 2
        ? "bg-white text-primary hover:bg-primary hover:text-white shadow-dramatic"
        : "bg-gray-300 text-gray-500 cursor-not-allowed"
    }`}
                  >
                    <span>Request Custom Quote</span>
                    <Icon name="ArrowRightIcon" size={20} />
                  </button>
                  {selectedServices.length === 1 && (
                    <p className="text-xs sm:text-sm text-center font-bold bg-red-50 rounded-xl text-red-600 mt-2">
                      Pick one more service to activate the button!
                    </p>
                  )}

                  {selectedServices.length >= 2 &&
                    selectedServices.length < 3 && (
                      <p className="text-xs text-center opacity-80">
                        Add one more service to unlock 15% discount!
                      </p>
                    )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal */}

        {showModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => {
              setShowModal(false);
              setQuoteSent(false);
            }}
          >
            <div
              className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Floating Header */}
              <div className="sticky top-0 bg-white z-10 p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-2xl font-bold text-foreground">
                    Request Your Quote
                  </h3>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setQuoteSent(false);
                    }}
                    className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 hover:text-gray-800 transition"
                  >
                    ✕
                  </button>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your email and review your custom quote
                </p>
              </div>

              {!quoteSent ? (
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)] space-y-6">
                  {/* Email Input */}
                  {/* Email Input */}
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full p-3 border rounded-xl focus:outline-none transition 
    ${emailError ? "border-red-600 focus:ring-red-500" : "border-border focus:ring-red-500"}`}
                    disabled={loading}
                  />

                  {/* Error Message */}
                  {emailError && (
                    <p className="text-xs text-red-600 mt-1">{emailError}</p>
                  )}

                  {/* Shadowed Quote Preview */}
                  <div className="p-4 bg-gray-50 rounded-xl shadow-md border border-gray-200">
                    <h4 className="font-semibold mb-2 text-gray-700">
                      Quote Preview
                    </h4>
                    <div
                      className="text-sm text-gray-800 space-y-1"
                      dangerouslySetInnerHTML={{
                        __html: `
                  <strong>Selected Services:</strong><br>${selectedDetails.replace(/\n/g, "<br>")}<br>
                  <strong>Subtotal:</strong> KES ${totalPrice.toLocaleString()}<br>
                  <strong>Bundle Discount:</strong> ${(bundleDiscount * 100).toFixed(0)}%<br>
                  <strong>Savings:</strong> KES ${savings.toLocaleString()}<br>
                  <strong>Total Investment:</strong> KES ${discountedPrice.toLocaleString()}
                `,
                      }}
                    />
                  </div>

                  {/* Send Button */}
                  <button
                    onClick={handleSendQuote}
                    disabled={loading}
                    className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition shadow-md"
                  >
                    {loading ? "Sending..." : "Send Request"}
                  </button>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <h3 className="text-2xl font-bold text-green-600 mb-2">
                    Quote Sent!
                  </h3>
                  <p className="text-gray-700">
                    You'll receive feedback in the next 24 hours.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state */}
        {selectedServices.length === 0 && (
          <div className="text-center py-2 sm:py-6 md:py-12">
            <Icon
              name="ArrowUp"
              size={32}
              className="text-red-600 shadow-subtle rounded-2xl mx-auto mb-2"
            />
            <p className="text-base sm:text-lg text-muted-foreground">
              Select from the services above to build your custom package
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServiceBundling;
